import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { ConvoAIErrorMessage } from '@atlassian/ai-agent-streaming/convo-ai-streaming-models';
import type { StreamError as ConvoAIStreamError } from '@atlassian/ai-agent-streaming/convo-ai-stream-messages';
import type { HookProps } from '@atlassian/ai-work-suggestion/types';

import { useAiIssueCreateAnalytics } from '../utils/useAiIssueCreateAnalytics.tsx';

export const isWhitelistedSLOError = (error: ConvoAIStreamError): boolean => {
	const whitelistedSLOErrors: Array<ConvoAIErrorMessage> = [
		ConvoAIErrorMessage.ACCEPTABLE_USE_VIOLATIONS,
		ConvoAIErrorMessage.NOT_ENOUGH_CONTENT_ERROR,
		ConvoAIErrorMessage.ANSWER_UNAVAILABLE_FOR_CONTENT,
		ConvoAIErrorMessage.NOT_FOUND,
		ConvoAIErrorMessage.BAD_REQUEST,
	];
	return whitelistedSLOErrors.includes(error.message.message_template as ConvoAIErrorMessage);
};

export const useStreamingHookProps = ({
	confluencePage,
	highlightedText,
	overrideXProduct,
}: {
	confluencePage: string;
	highlightedText: string;
	overrideXProduct?: string;
}) => {
	const { fireTrack } = useAiIssueCreateAnalytics();
	const { locale } = useIntl();
	return useMemo(
		() => ({
			analyticsConfig: {
				aiFeatureName: 'confluenceAiWorkCreationConvoAI',
				product: 'confluence',
				subproduct: 'jira',
				useDefaultGASv3EventSchema: false,
				source: 'highlightActionsMenu',
				skipAISessionValidation: true,
			},
			requestHeaderOverrides: {
				overrideXProduct,
			},
			issueConfig: {
				max_issues: 1,
				suggested_issue_field_types: [
					{
						issue_type: 'Task',
						fields: [
							{
								field_name: 'Summary',
								field_type: 'Short-Text',
							},
							{
								field_name: 'Description',
								field_type: 'Paragraph',
							},
						],
					},
				],
			},
			experienceId: 'ai-issue-create-confluence-single',
			sourceType: 'documentation',
			input: {
				source: 'CONFLUENCE',
				context: {
					primary_text: confluencePage,
					highlighted_text: highlightedText,
				},
				locale,
			},
			onCompletion: () => {},
			customExperienceTracker: {
				start: () => {
					fireTrack('aiIssueCreateFetchSuggestedFieldsRequest start', {
						locale,
					});
				},
				succeed: () => {
					fireTrack('aiIssueCreateFetchSuggestedFieldsRequest complete', {
						haveResult: true,
						locale,
					});
				},
				fail: (convoAIStreamError: ConvoAIStreamError) => {
					fireTrack('aiIssueCreateFetchSuggestedFieldsRequest fail', {
						errorMessage: convoAIStreamError?.message?.content,
						statusCode: convoAIStreamError?.message?.status_code,
						message_template: convoAIStreamError?.message?.message_template,
						shouldSlo: !isWhitelistedSLOError(convoAIStreamError),
						locale,
					});
				},
				abort: () => {
					fireTrack('aiIssueCreateFetchSuggestedFieldsRequest abort', {
						locale,
					});
				},
			},
		}),
		[confluencePage, highlightedText, overrideXProduct, fireTrack, locale],
	) as HookProps;
};
